import React, { useState, useEffect, useRef } from "react";
import cookieService from "../services/cookieService";
import CookieSettings from "./CookieSettings";
import { useDetectedBrandLegal } from "../hooks/useDetectedBrandLegal";
import { LegalLink } from "./legal/LegalLink";

interface CookieBannerProps {
  onConsentChange?: () => void;
}

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

/**
 * Max retry attempts for failed consent API calls.
 * Synced with `packages/cookie-consent-core/src/api-client.ts:MAX_RETRY_ATTEMPTS = 5`.
 */
const MAX_RETRY_ATTEMPTS = 5;
/** Retry delay in ms */
const RETRY_DELAY_MS = 30_000;
/** localStorage key for pending consent */
const PENDING_CONSENT_KEY = "cookie_consent_pending";

type RetryActionType = "all" | "essential" | "custom";

const CookieBanner: React.FC<CookieBannerProps> = ({ onConsentChange }) => {
  // ADR-088 / spec 2026-07-06-per-brand-legal-content (AC B, H4): the ONLY
  // source for this URL is GET /brands/detect — NOT the hardcoded
  // kiedynabory.pl literal, which was wrong for HotelB2B visitors.
  const { privacyUrl, isLoading: legalLoading } = useDetectedBrandLegal();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  /**
   * Per-action retry counter. Each action ("all" / "essential" / "custom") has its
   * own attempt count so a failed acceptAll does not exhaust retries for a later
   * essentialOnly click. Using `useRef` (not state) because we don't render the
   * counter and we want the read-after-write semantics inside the same handler.
   */
  const retryCountsRef = useRef<Record<RetryActionType, number>>({
    all: 0,
    essential: 0,
    custom: 0,
  });
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
  });
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Check if consent has been given
    const hasConsent = cookieService.hasConsent();
    setShowBanner(!hasConsent);

    // Load existing preferences if available
    const existingConsent = cookieService.getLocalConsent();
    if (existingConsent) {
      setPreferences({
        essential: existingConsent.essential,
        analytics: existingConsent.analytics,
        marketing: existingConsent.marketing,
      });
    }
  }, []);

  const storePendingConsent = (type: string, prefs?: CookiePreferences) => {
    localStorage.setItem(
      PENDING_CONSENT_KEY,
      JSON.stringify({ type, prefs, timestamp: Date.now() })
    );
  };

  const clearPendingConsent = () => {
    localStorage.removeItem(PENDING_CONSENT_KEY);
  };

  const scheduleRetry = (action: () => Promise<void>, attempt: number) => {
    if (attempt >= MAX_RETRY_ATTEMPTS) return;
    setTimeout(() => {
      action();
    }, RETRY_DELAY_MS);
  };

  const handleConsentAction = async (
    action: RetryActionType,
    apiCall: () => Promise<unknown>,
    onSuccess?: () => void,
    prefs?: CookiePreferences
  ): Promise<void> => {
    if (saving) return;
    setSaving(true);
    setSaveError(false);
    storePendingConsent(action, prefs);
    try {
      await apiCall();
      clearPendingConsent();
      retryCountsRef.current[action] = 0;
      onSuccess?.();
      setShowBanner(false);
      onConsentChange?.();
    } catch {
      setSaveError(true);
      const nextAttempt = retryCountsRef.current[action] + 1;
      retryCountsRef.current[action] = nextAttempt;
      scheduleRetry(
        () => handleConsentAction(action, apiCall, onSuccess, prefs),
        nextAttempt
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptAll = () =>
    handleConsentAction("all", () => cookieService.acceptAll());

  const handleAcceptEssential = () =>
    handleConsentAction("essential", () => cookieService.acceptEssentialOnly());

  const handleShowSettings = () => {
    setShowSettings(true);
  };

  const handlePreferencesChange = (newPreferences: CookiePreferences) => {
    setPreferences(newPreferences);
  };

  const handleSettingsSave = () =>
    handleConsentAction(
      "custom",
      () =>
        cookieService.acceptCustom(
          preferences.analytics,
          preferences.marketing
        ),
      () => setShowSettings(false),
      preferences
    );

  const handleSettingsClose = () => {
    setShowSettings(false);
    // Reset preferences to saved values
    const existingConsent = cookieService.getLocalConsent();
    if (existingConsent) {
      setPreferences({
        essential: existingConsent.essential,
        analytics: existingConsent.analytics,
        marketing: existingConsent.marketing,
      });
    }
    // Return focus to settings button
    settingsButtonRef.current?.focus();
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50"
        role="region"
        aria-label="Ustawienia prywatności i cookies"
        aria-live="polite"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {saveError && (
            <div
              role="alert"
              aria-live="polite"
              className="mb-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded"
            >
              Nie udało się zapisać preferencji, ponawiamy próbę.
            </div>
          )}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Szanujemy Twoją prywatność
              </h3>
              <p className="text-sm text-gray-600">
                Używamy cookies aby zapewnić najlepsze doświadczenia na naszej
                stronie. Cookies niezbędne są zawsze aktywne, ale możesz wybrać
                inne kategorie według własnych preferencji. Więcej informacji
                znajdziesz w naszej{" "}
                <LegalLink
                  url={privacyUrl}
                  label="Polityce prywatności"
                  isLoading={legalLoading}
                  unavailableTestId="cookie-banner-privacy-link-unavailable"
                  className="text-primary-700 hover:underline"
                />
                .
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              {/* EROD 03/2022 (https://www.edpb.europa.eu/our-work-tools/our-documents/guidelines/guidelines-032022-deceptive-design-patterns-social-media_en):
                  visual equality — both buttons use border-2, same size/padding */}
              <button
                onClick={handleAcceptEssential}
                disabled={saving}
                className="px-4 py-2 text-sm border-2 border-gray-400 text-gray-700 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                Tylko niezbędne
              </button>
              <button
                ref={settingsButtonRef}
                onClick={handleShowSettings}
                disabled={saving}
                className="px-4 py-2 text-sm border-2 border-primary-600 text-primary-700 rounded-md hover:bg-primary-50 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                Ustawienia
              </button>
              <button
                onClick={handleAcceptAll}
                disabled={saving}
                className="px-4 py-2 text-sm border-2 border-primary-600 bg-primary-700 text-white rounded-md hover:bg-primary-800 hover:border-primary-700 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                Akceptuj wszystkie
              </button>
            </div>
          </div>
        </div>
      </div>

      {showSettings && (
        <CookieSettings
          preferences={preferences}
          onPreferencesChange={handlePreferencesChange}
          onSave={handleSettingsSave}
          onClose={handleSettingsClose}
        />
      )}
    </>
  );
};

export default CookieBanner;
